/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { IDialogContentStyleProps, IDialogContentStyles, IDialogStyleProps, IDialogStyles } from '@fluentui/react/lib/Dialog';
import { Image, ImageLoadState } from '@fluentui/react/lib/Image';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { Carousel, CarouselSlide } from 'src/core/components/Carousel';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

const messages = defineMessages({
    welcomeSlideTitle: {
        id: 'languageStudio.languageStudioOnboardingModal.welcomeSlideTitle',
        defaultMessage: 'Welcome to the new Language Studio'
    },
    welcomeSlideContent: {
        id: 'languageStudio.languageStudioOnboardingModal.welcomeSlideContent',
        defaultMessage: 'All the tools you need to identify and analyze content within text'
    },
    moreCapabilitiesSlideTitle: {
        id: 'languageStudio.languageStudioOnboardingModal.moreCapabilitiesSlideTitle',
        defaultMessage: 'More capabilities'
    },
    moreCapabilitiesSlideContent: {
        id: 'languageStudio.languageStudioOnboardingModal.moreCapabilitiesSlideContent',
        defaultMessage: `Try out capabilities with samples or your own data. Easily
                          explore what’s possible with Language Studio - zero code needed.`
    },
    customizationSlideTitle: {
        id: 'languageStudio.languageStudioOnboardingModal.customizationSlideTitle',
        defaultMessage: 'Customize for your business needs'
    },
    customizationSlideContent: {
        id: 'languageStudio.languageStudioOnboardingModal.customizationSlideContent',
        defaultMessage: `Begin with a starter pre-built capability or customize your own
                          from scratch by uploading your own training data and defining your own model.`
    },
    chooseResource: {
        id: 'languageStudio.languageStudioOnboardingModal.chooseResource',
        defaultMessage: 'Choose resource'
    },
    doLater: {
        id: 'languageStudio.languageStudioOnboardingModal.doLater',
        defaultMessage: 'Do this later'
    },
    getStartedSlideTitle: {
        id: 'languageStudio.languageStudioOnboardingModal.getStartedSlideTitle',
        defaultMessage: 'Get started with Language Studio'
    },
    getStartedSlideContent: {
        id: 'languageStudio.languageStudioOnboardingModal.getStartedSlideContent',
        defaultMessage: `You’ll need a Cognitive Service or Language resource associated with your
                          Azure account to begin. You can select one you already have or create a new resource.`
    },
    slideImageAltText: {
        id: 'languageStudio.languageStudioOnboardingModal.slideImageAltText',
        defaultMessage: '{slideName} image.'
    }
});

const theme = getScopedTheme('LanguageStudioOnboardingModal');
const { styleDiv } = getStylistV2('LanguageStudioOnboardingModal');
const stackStyler = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>();

const slideContentStyles = stackStyler({ root: { textAlign: 'center', minHeight: 150, maxWidth: 580 } });
const slideHeaderStyles = stackStyler({ root: { minHeight: 237, backgroundColor: theme.slideHeaderBackgroundColor } });
const skipButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { color: theme.slidePrimaryTextColor } });
const modalStyles = classNamesFunction<IDialogStyleProps, IDialogStyles>()({
    main: { boxShadow: 'none', backgroundColor: 'transparent', width: 'auto' }
});
const dialogStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    topButton: { marginRight: '30px' },
    button: { color: `${theme.defaultColor} !important`, ':hover': { color: `${theme.slidePrimaryTextColor} !important` } }
});
const FooterContainer = styleDiv('FootContainer', {
    display: 'grid',
    paddingBottom: 10,
    justifyItems: 'center',
    gridTemplateColumns: '1fr auto 1fr'
});

const getSlideContent = (titleText: string, contentText: string) => {
    const slideTitleTextProps: LuisTextProps = { variant: 'xxLargePlus', color: theme.slidePrimaryTextColor };

    const slideContentContainerProps: IStackProps = {
        horizontalAlign: 'center',
        styles: slideContentStyles,
        tokens: { childrenGap: 8, padding: '24px 20px' }
    };

    return (
        <Stack {...slideContentContainerProps}>
            <EditableTitle titleTextProps={slideTitleTextProps} title={titleText} />
            <LuisText color={theme.slideTextColor}>{contentText}</LuisText>
        </Stack>
    );
};

export const LanguageStudioOnboardingModal = (props: BasicModalProps<object>) => {
    const { onDismiss } = props;
    const { localize } = useLocalization();

    const [isImageLoading, setIsImageLoading] = React.useState(false);
    const [currentSlideKey, setCurrentSlideKey] = React.useState('welcome');

    const getSlideHeader = (imageName: string) => (
        <Stack grow horizontalAlign="center" verticalAlign="center" styles={slideHeaderStyles}>
            <Image
                width="100%"
                shouldStartVisible
                alt={localize(messages.slideImageAltText, { slideName: imageName })}
                src={getAssetsUrl(`assets/images/languageStudio/onboardModal/${imageName}.svg`)}
                onLoadingStateChange={loadState => setIsImageLoading(loadState !== ImageLoadState.loaded)}
            />
            {isImageLoading && <Spinner size={SpinnerSize.large} />}
        </Stack>
    );

    const skipModal = () => {
        getStorage('localStorage').setData(LuisConstants.StorageKeys.LanguageStudioOnboardingModalSeen, 'true');
        onDismiss?.();
    };

    const slides: CarouselSlide[] = [
        {
            key: 'welcome',
            header: getSlideHeader('welcomeSlideImage'),
            content: getSlideContent(localize(messages.welcomeSlideTitle), localize(messages.welcomeSlideContent)),
            announcedMessage: `${localize(messages.welcomeSlideTitle)}, ${localize(messages.welcomeSlideContent)}`
        },
        {
            key: 'moreCapabilities',
            header: getSlideHeader('capabilitiesSlideImage'),
            content: getSlideContent(localize(messages.moreCapabilitiesSlideTitle), localize(messages.moreCapabilitiesSlideContent)),
            announcedMessage: `${localize(messages.moreCapabilitiesSlideTitle)}, ${localize(messages.moreCapabilitiesSlideContent)}`
        },
        {
            key: 'customization',
            header: getSlideHeader('customizationSlideImage'),
            content: getSlideContent(localize(messages.customizationSlideTitle), localize(messages.customizationSlideContent)),
            announcedMessage: `${localize(messages.customizationSlideTitle)}, ${localize(messages.customizationSlideContent)}`
        },
        {
            key: 'getStarted',
            header: getSlideHeader('getStartedSlideImage'),
            content: getSlideContent(localize(messages.getStartedSlideTitle), localize(messages.getStartedSlideContent)),
            contentFooter: (
                <FooterContainer>
                    <Stack verticalAlign="center" styles={{ root: { gridColumnStart: '2' } }}>
                        <PrimaryButton
                            onClick={props.onSubmit}
                            text={localize(messages.chooseResource)}
                            data-automation-id="onboardingModalChooseResource"
                        />
                    </Stack>

                    <Stack.Item styles={{ root: { marginLeft: 'auto' } }}>
                        <ActionButton text={localize(messages.doLater)} styles={skipButtonStyles} onClick={skipModal} />
                    </Stack.Item>
                </FooterContainer>
            ),
            announcedMessage: `${localize(messages.getStartedSlideTitle)}, ${localize(messages.getStartedSlideContent)}`
        }
    ];

    return (
        <BasicModal
            {...props}
            styles={modalStyles}
            onDismiss={skipModal}
            shouldRenderFooter={false}
            dialogContentProps={{ styles: dialogStyles }}>
            <Carousel infiniteLoop={false} activeSlideKey={currentSlideKey} onSlideChange={setCurrentSlideKey} slides={slides} />
        </BasicModal>
    );
};
